import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { ListingCard } from "@/components/site/ListingCard";
import {
  getCategory,
  listingsByCategory,
  locations,
  comboHref,
  categories,
  blogPosts,
  blogHref,
} from "@/lib/data";
import { ArrowRight } from "lucide-react";
import { generateBreadcrumbSchema, generateWebPageSchema } from "@/lib/utils";

const SITE_URL = "https://letslistbusiness.com";

export const Route = createFileRoute("/category/$slug/")({
  loader: ({ params }) => {
    const cat = getCategory(params.slug);
    if (!cat) throw notFound();
    return { cat };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { cat } = loaderData;
    const title = `${cat.name} — Find the Best ${cat.name} Near You | LetsListBusiness`;
    const description = `Discover top-rated ${cat.name.toLowerCase()} across the country. Browse verified listings, real reviews and recommendations from locals.`;
    const url = `${SITE_URL}/category/${cat.slug}/`;
    
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: `Best ${cat.name} | LetsListBusiness` },
        { property: "og:description", content: cat.description },
        { property: "og:image", content: cat.image },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `Best ${cat.name} | LetsListBusiness` },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: cat.image },
      ],
      links: [{ rel: "canonical", href: url }],
      schema: [
        generateWebPageSchema(title, description, url),
        generateBreadcrumbSchema([
          { label: "Home", href: "/" },
          { label: "Categories", href: "/category/" },
          { label: cat.name, href: `/category/${cat.slug}/` },
        ], SITE_URL),
      ],
    };
  },
  component: CategoryPage,
  notFoundComponent: () => (
    <div className="container mx-auto px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-bold">Category not found</h1>
      <Link to="/category" className="mt-4 inline-block text-primary">Browse all categories</Link>
    </div>
  ),
});

function CategoryPage() {
  const { cat } = Route.useLoaderData();
  const items = listingsByCategory(cat.slug);
  const relatedPosts = blogPosts.filter((p) => p.category === cat.slug).slice(0, 3);
  const otherCats = categories.filter((c) => c.slug !== cat.slug).slice(0, 6);

  return (
    <>
      <Breadcrumbs
        items={[
          { label: "Home", href: "/" },
          { label: "Categories", href: "/category/" },
          { label: cat.name },
        ]}
      />

      {/* Split hero — image left, copy right */}
      <section className="container mx-auto px-4 pb-12">
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <div className="overflow-hidden rounded-3xl shadow-card-lg">
              <img src={cat.image} alt={cat.name} className="aspect-4/3 w-full object-cover" />
            </div>
          </div>
          <div className="flex flex-col justify-center lg:col-span-5">
            <div className="text-5xl">{cat.icon}</div>
            <div className="mt-3 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              Category · {items.length} listings
            </div>
            <h1 className="mt-3 font-display text-5xl font-bold leading-[1.02] sm:text-6xl">{cat.name}</h1>
            <p className="mt-5 text-lg text-muted-foreground">{cat.description}</p>
            <div className="mt-6 flex flex-wrap gap-2 text-xs">
              {locations.slice(0, 3).map((l) => (
                <Link key={l.slug} {...comboHref(l.slug, cat.slug)} className="rounded-full bg-primary/10 px-3 py-1.5 font-medium text-primary hover:bg-primary hover:text-primary-foreground transition-colors">
                  In {l.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Listings + sticky sidebar */}
      <section className="container mx-auto px-4 pb-16">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="mb-6 flex items-end justify-between border-b border-border pb-4">
              <h2 className="font-display text-2xl font-bold">All {items.length} {cat.name.toLowerCase()}</h2>
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Sorted by rating</span>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              {items.slice(0, 18).map((l) => (
                <ListingCard key={l.id} listing={l} />
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 space-y-6">
              <div className="rounded-2xl border border-border bg-surface p-6">
                <h3 className="font-display text-lg font-semibold">{cat.name} by city</h3>
                <p className="mt-1 text-xs text-muted-foreground">Jump to your city.</p>
                <ul className="mt-4 space-y-1">
                  {locations.map((l) => (
                    <li key={l.slug}>
                      <Link {...comboHref(l.slug, cat.slug)} className="group flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:bg-card">
                        <span>{l.name}, {l.state}</span>
                        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-lg font-semibold">Other categories</h3>
                <ul className="mt-4 space-y-2 text-sm">
                  {otherCats.map((c) => (
                    <li key={c.slug}>
                      <Link to="/category/$slug" params={{ slug: c.slug }} className="flex items-center gap-3 hover:text-primary">
                        <span className="text-lg">{c.icon}</span>
                        <span className="font-medium">{c.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>
      {/* Related Articles Section */}
      {relatedPosts.length > 0 && (
        <section className="bg-surface py-20">
          <div className="container mx-auto px-4">
            <div className="mb-12 flex items-end justify-between">
              <div className="max-w-xl">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">02 — Expert Guides</div>
                <h2 className="mt-4 font-display text-4xl font-bold leading-tight">Expert insights for {cat.name.toLowerCase()}</h2>
              </div>
              <Link to="/blog" className="hidden text-sm font-bold text-primary hover:underline sm:block">View all articles →</Link>
            </div>
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {relatedPosts.map((post) => (
                <Link
                  key={post.slug}
                  {...blogHref(post.slug)}
                  className="group flex flex-col overflow-hidden rounded-4xl border border-border bg-card shadow-card hover:-translate-y-1 hover:shadow-card-lg transition-all duration-300"
                >
                  <div className="aspect-16/10 overflow-hidden">
                    <img src={post.image} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </div>
                  <div className="flex flex-1 flex-col p-8">
                    <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-primary">
                      <span>{new Date(post.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                    </div>
                    <h3 className="mt-4 font-display text-xl font-bold leading-tight group-hover:text-primary transition-colors">{post.title}</h3>
                    <p className="mt-4 line-clamp-3 text-sm leading-relaxed text-muted-foreground">{post.excerpt}</p>
                    <div className="mt-auto pt-6 text-xs font-bold uppercase tracking-wider text-foreground">Read more →</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
